import type { OwnedItem, ItemInput } from "../types"
import { useLayoutEffect, useState } from "react"
import { useAuth } from "../contexts/AuthContext"
import NavBar from "../components/NavBar"
import ItemForm from "../components/ItemForm"
import OwnedItemRow from "../components/OwnedItemRow"
import { useOwnedList } from "../hooks/useOwnedList"

export default function DashboardPage() {
  const { currentUser } = useAuth()
  const { list, loading, add, update, remove } = useOwnedList(currentUser?.id)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingItem, setEditingItem] = useState<OwnedItem | null>(null)

  const [focusTarget, setFocusTarget] = useState<string | null>(null)
  const [announcement, setAnnouncement] = useState("")
  useLayoutEffect(() => {
    if (focusTarget) {
      document.getElementById(focusTarget)?.focus()
      setFocusTarget(null)
    }
  }, [focusTarget])

  async function handleAddItem(itemData: ItemInput) {
    const item = await add(itemData)
    setAnnouncement("Gift added")
    setFocusTarget(`gift-${item.id}`)
    setShowAddForm(false)
  }

  async function handleUpdateItem(itemId: number, itemData: ItemInput) {
    await update(itemId, itemData)
    setEditingItem(null)
    setAnnouncement("Gift updated")
    setFocusTarget(`gift-${itemId}`)
  }

  function renderGiftList() {
    if (loading) {
      return <p className="text-sm text-gray-500">Loading...</p>
    }
    if (!list) {
      return (
        <p className="text-sm text-red-500">
          Unable to load your list. Please refresh the page.
        </p>
      )
    }
    return (
      <div className="space-y-3">
        {editingItem && <ItemForm initialValues={editingItem} onSubmit={data => handleUpdateItem(editingItem.id, data)} onCancel={() => { setFocusTarget(`edit-${editingItem.id}`); setEditingItem(null) }} submitLabel="Save changes" />}
        {showAddForm && (
          <ItemForm onSubmit={handleAddItem} onCancel={() => { setShowAddForm(false); setFocusTarget("add-gift") }} submitLabel="Add gift" />
        )}
        {list.items.length === 0 && !showAddForm ? (
          <div className="bg-white rounded-xl p-4">
            <p>No items yet. Add your first gift idea!</p>
            <button className="min-h-11 px-3 text-blue-600" onClick={() => setShowAddForm(true)}>Add your first gift</button>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3 items-start">
            {list.items.map((item) =>
                <OwnedItemRow
                  key={item.id}
                  item={item}
                  editingDisabled={showAddForm || editingItem !== null}
                  onEdit={() => {
                    setShowAddForm(false)
                    setEditingItem(item)
                  }}
                  onDelete={async () => {
                    const index = list.items.findIndex(value => value.id === item.id)
                    const neighbor = list.items[index + 1] || list.items[index - 1]
                    await remove(item.id)
                    setAnnouncement("Gift removed")
                    setFocusTarget(neighbor ? `gift-${neighbor.id}` : "add-gift")
                  }}
                />
            )}
          </div>
        )}


      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-2xl lg:max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Gift list section */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Your Gift List</h1>
            {list && !loading && !showAddForm && !editingItem && <button id="add-gift" onClick={() => setShowAddForm(true)} className="min-h-11 px-4 bg-blue-600 text-white rounded-lg">Add a gift</button>}
          </div>
          <p role="status" className="sr-only">{announcement}</p>

          {renderGiftList()}
        </section>

      </main>
    </div>
  )
}
